import type { Survey, SurveyViewingPermission } from '@engagement-web-app/common';
import type { UseQueryResult } from '@tanstack/react-query';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Form } from '@/components/Form';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import * as styles from './index.css';

type SurveySelectorProps = {
    surveys: Survey[];
    /** `unrestricted` grants view everything — nothing is rendered once that resolves true. */
    permissions: UseQueryResult<{ unrestricted: boolean; permissions: SurveyViewingPermission[] }>;
    selectedSurveyId: string;
    onSelectSurvey: (surveyId: string) => void;
    /** Disables selection, e.g. while a save/revoke for the current survey is in flight. */
    disabled?: boolean;
};

export function SurveySelector({
    surveys,
    permissions,
    selectedSurveyId,
    onSelectSurvey,
    disabled,
}: SurveySelectorProps) {
    if (permissions.isError) {
        return <ErrorOverlay active onRetry={permissions.refetch} message="閲覧権限の取得に失敗しました。" />;
    }
    if (permissions.isPending) return <SurveySelectorSkeleton />;
    if (permissions.data.unrestricted) return null;

    return (
        <SurveySelectorContent
            surveys={surveys}
            permissions={permissions.data.permissions}
            selectedSurveyId={selectedSurveyId}
            onSelectSurvey={onSelectSurvey}
            disabled={disabled}
        />
    );
}

type SurveySelectorContentProps = Omit<SurveySelectorProps, 'permissions'> & {
    permissions: SurveyViewingPermission[];
};

// Split from the buffer above so its rendering stays free of loading concerns.
function SurveySelectorContent({
    surveys,
    permissions,
    selectedSurveyId,
    onSelectSurvey,
    disabled,
}: SurveySelectorContentProps) {
    const permissionBySurveyId = new Map(permissions.map((permission) => [permission.surveyId, permission]));

    return (
        <Section pt="8">
            <SectionHeader
                title="サーベイ選択"
                description="サーベイごとに、閲覧の可否・コメント閲覧の可否・フィルタに使える属性を設定します。"
            />

            <Form.Field
                control={(props) => (
                    <Select
                        {...props}
                        className={styles.surveySelect}
                        value={selectedSurveyId}
                        onChange={(e) => onSelectSurvey(e.target.value)}
                        disabled={disabled}
                    >
                        {surveys.map((survey) => (
                            <option key={survey.surveyId} value={survey.surveyId}>
                                {survey.surveyName}（{permissionBySurveyId.has(survey.surveyId) ? '閲覧可' : '閲覧不可'}
                                ）
                            </option>
                        ))}
                    </Select>
                )}
            />
        </Section>
    );
}

export function SurveySelectorSkeleton() {
    return (
        <Section pt="8">
            <SectionHeader
                title="サーベイ選択"
                description="サーベイごとに、閲覧の可否・コメント閲覧の可否・フィルタに使える属性を設定します。"
            />

            <Form.FieldSkeleton label={false} />
        </Section>
    );
}
